// @flow
import React from 'react';
import {isEmpty, get} from 'lodash';
import {connect} from 'react-redux';
import {isDated} from '../../utils/searchParamsUtils';
import {useFetchRecentlyViewedListings} from '../../hooks/useFetchRecentlyViewedListings/useFetchRecentlyViewedListings';
import {useFetchJudgeSerpData} from '../../hooks/useFetchjudgeSerpData/useFetchJudgeSerpData';
import {useFetchAmenityCarouselListings} from '../../hooks/useFetchAmenityCarouselListings/useFetchAmenityCarouselListings';
import {useFetchFlexDatesCarouselListings} from '../../hooks/useFetchFlexDatesCarouselListings/useFetchFlexDatesCarouselListings';
import transformReduxParamsToSearch from '../../queries/transformReduxParamsToSearchRequest';
import CarouselsContainer from './CarouselsContainer';
import {getDeviceType} from './utils/leadCarouselUtils';
import {
    SERP_AMENITIES_CAROUSEL,
    SERP_PREVIOUSLY_VIEWED_CAROUSEL,
    SERP_FLEX_DATES_CAROUSEL,
    EdapCarouselPlacementsMap
} from './utils/constants';
import type {
    ParsedSearchParams,
    PercentBookedResult
} from '../../queries/searchRequestQuery';

import {buildSeeAllLink} from '../AmenityCarousel/utils/buildSeeAllLink';

type OwnProps = {
    placement: string,
    resultCount: number,
    showMabCarousels: boolean,
    percentBooked: PercentBookedResult,
    isLoadingOrRedirecting: boolean,
    results: any
};

type StateProps = {
    isDatedSearch: boolean,
    isMobile: boolean,
    isTablet: boolean,
    parsedParams: ParsedSearchParams,
    adultsCount: number,
    childrenCount: number,
    searchParams: any,
    locale: string
};

type Props = OwnProps & StateProps;

const skipQuery = (judgeResult, carousel) => {
    return !judgeResult.some((item) => item.component === carousel);
};

const LeadCarouselContainer = ({
    isDatedSearch,
    isMobile,
    isTablet,
    parsedParams,
    placement,
    resultCount,
    showMabCarousels = true,
    results,
    searchParams,
    percentBooked,
    adultsCount,
    childrenCount,
    isLoadingOrRedirecting,
    locale
}: Props) => {
    const deviceType = getDeviceType(isMobile, isTablet);
    const carouselPosition = EdapCarouselPlacementsMap[placement];

    const judgeSerpData = useFetchJudgeSerpData({
        resultCount,
        isDatedSearch,
        deviceType,
        numFilters: searchParams.numFilters,
        percentBooked,
        adultsCount,
        childrenCount,
        isLoadingOrRedirecting
    });

    const judgeResult = get(judgeSerpData, ['result'], []);

    const skipPreviouslyViewedSearchQuery =
        skipQuery(judgeResult, SERP_PREVIOUSLY_VIEWED_CAROUSEL) ||
        !showMabCarousels;
    const {
        previouslyViewedListings,
        previouslyViewedListingsLoading
    } = useFetchRecentlyViewedListings(
        parsedParams,
        isMobile,
        skipPreviouslyViewedSearchQuery
    );

    const skipPopularAmenitySearchQuery =
        skipQuery(judgeResult, SERP_AMENITIES_CAROUSEL) || !showMabCarousels;
    const {
        amenityCarouselData,
        amenityCarouselDataLoading
    } = useFetchAmenityCarouselListings(
        searchParams,
        isMobile,
        parsedParams,
        results,
        skipPopularAmenitySearchQuery
    );

    const skipFlexibleDateListingsQuery =
        skipQuery(judgeResult, SERP_FLEX_DATES_CAROUSEL) || !showMabCarousels;
    const {
        flexibleDateListings,
        flexibleDateListingsLoading
    } = useFetchFlexDatesCarouselListings(
        parsedParams,
        results,
        skipFlexibleDateListingsQuery
    );

    const hasAmenityCarouselListings = !isEmpty(amenityCarouselData);
    const hasPrevViewedListings = !isEmpty(previouslyViewedListings);
    const hasFlexibleDatesListings = !isEmpty(flexibleDateListings);
    const carouselRequestsAreFinished =
        !previouslyViewedListingsLoading &&
        !amenityCarouselDataLoading &&
        !flexibleDateListingsLoading;

    // TODO: move chosenFilter and seeMoreUrl props to AmenityCarousel component's code
    const chosenFilter = get(
        amenityCarouselData,
        `popularAmenitySearch.chosenFilter`,
        ''
    );
    const seeMoreUrl = hasAmenityCarouselListings
        ? buildSeeAllLink(searchParams, chosenFilter)
        : '';

    if (
        carouselRequestsAreFinished &&
        (hasAmenityCarouselListings ||
            hasPrevViewedListings ||
            hasFlexibleDatesListings)
    ) {
        return (
            <CarouselsContainer
                locale={locale}
                isMobile={isMobile}
                judgeResult={judgeResult}
                placement={placement}
                carouselPosition={carouselPosition}
                hasPrevViewedListings={hasPrevViewedListings}
                hasAmenityCarouselListings={hasAmenityCarouselListings}
                hasFlexibleDatesListings={hasFlexibleDatesListings}
                previouslyViewedListings={previouslyViewedListings}
                amenityCarouselData={amenityCarouselData}
                flexibleDateListings={flexibleDateListings}
                seeMoreUrl={seeMoreUrl}
            />
        );
    }

    return null;
};

const mapStateToProps = (state) => {
    const {searchParams, system} = state;
    const {isMobile, isTablet, locale} = system;
    const {params} = searchParams;
    const isDatedSearch = !!isDated(params);
    const parsedParams = transformReduxParamsToSearch(params);

    return {
        adultsCount: params.adultsCount,
        childrenCount: params.childrenCount,
        isDatedSearch,
        isMobile,
        isTablet,
        parsedParams,
        searchParams,
        locale
    };
};

export default connect(mapStateToProps)(LeadCarouselContainer);
